/**
 * The settlement planner.
 *
 * Pure: no React, no component state, no queries. It is its own module for the
 * same reason `src/lib/book.ts` is — the maths that decides how much money moves
 * has to be provable against hand-worked examples, not inferred from a rendered
 * table. `settle.test.ts` pins every rule below.
 *
 * It deliberately does NOT re-derive caps. `settlementPlan` in `@/lib/book` is
 * the single source of cap truth, and `settlementCapReason` / `settlementSkipReason`
 * in `@/data/api` are the single source of the SENTENCES — the plan table, the
 * live run and the report all quote them, and a cap explained one way in the
 * plan and another in the report is a support ticket. That is the whole reason
 * this file imports two pure string builders from the data layer: it is cheaper
 * to justify one import than to explain to an operator why the same cap has two
 * names.
 */

import { availableToGive, floor2, round2, settlementPlan } from '@/lib/book'
import { settlementCapReason, settlementSkipReason } from '@/data/api'
import { money } from '@/lib/format'
import type { Account } from '@/data/types'

/**
 * Round DOWN to the paise, never round.
 *
 * Re-exported from `@/lib/book` rather than redefined: the naive
 * `Math.floor(n * 100) / 100` loses a paise off already-clean figures
 * (`8.29 * 100` is 828.9999999999999 in IEEE 754), and a second copy of this on
 * the page is how the plan and the ledger start disagreeing by a paise.
 */
export { floor2 }

/**
 * Credit only ever moves between an account and its DIRECT parent. One
 * predicate, imported everywhere, gates the Give/Take buttons, the
 * click-to-zero settlement cell and row selectability — three surfaces that
 * must never disagree about who is settleable.
 */
export const isDirect = (row: Account, self: Account | undefined): boolean =>
  self != null && row.parentId === self.id

export interface PlannedRow {
  account: Account
  direction: 'give' | 'take'
  /** |settlement| — what zeroing this row requires. */
  intended: number
  /** What the caps allow at the moment this row runs. */
  planned: number
  /** intended - planned. */
  remaining: number
  /** Non-null whenever planned < intended. Names the cap that bit, never "capped". */
  capReason: string | null
  /** Non-blocking. Currently one case: a take that strips committed credit. */
  warnings: string[]
  /** Set only when planned === 0 — the row will not move at all. */
  skipReason: string | null
}

/**
 * The movement that takes one row's settlement to exactly zero.
 *
 * `settlement` is signed from the PARENT's point of view: positive means they
 * owe you, so you take; negative means you owe them, so you give. Null for an
 * already-settled row — a zero-amount transfer is not a movement, and offering
 * one is how an operator settles the same account twice on a Monday.
 */
export function zeroingMove(
  row: Pick<Account, 'settlement'>,
): { direction: 'give' | 'take'; amount: number } | null {
  const settlement = round2(row.settlement)
  if (settlement === 0) return null
  return settlement > 0
    ? { direction: 'take', amount: settlement }
    : { direction: 'give', amount: round2(-settlement) }
}

export interface CapForResult {
  /** The most that can move in this direction right now, floored to the paise. */
  cap: number
  /** The sentence the dialog shows under the amount field. Always names the constraint. */
  because: string
  /** Your balance less what is already committed to open bets. */
  freeToGive: number
  /** Their room under their own credit limit. */
  headroom: number
}

/**
 * The cap for a single movement, and the sentence that explains it.
 *
 * This mirrors `settlementPlan`'s two caps rather than calling it, because
 * `settlementPlan` reads the direction off the settlement's SIGN and therefore
 * cannot answer "what if I gave to an account that already owes me" — which is
 * exactly what the transfer dialog asks the moment the operator flips the
 * toggle. `settle.test.ts` pins the two against each other whenever the
 * direction does match the sign, so the mirror cannot drift.
 */
export function capFor(
  self: Pick<Account, 'balance' | 'exposure'>,
  account: Pick<Account, 'username' | 'balance' | 'creditLimit'>,
  direction: 'give' | 'take',
): CapForResult {
  const freeToGive = availableToGive(self.balance, self.exposure)
  const headroom = Math.max(0, floor2(account.creditLimit - account.balance))

  if (direction === 'take') {
    const cap = Math.max(0, floor2(account.balance))
    return {
      cap,
      freeToGive,
      headroom,
      because:
        cap <= 0
          ? `${account.username} holds ${money(account.balance)}, so there is nothing to take.`
          : `Up to ${money(cap)} — everything ${account.username} currently holds.`,
    }
  }

  const cap = Math.min(freeToGive, headroom)
  return {
    cap,
    freeToGive,
    headroom,
    because:
      cap <= 0
        ? `Nothing can be given right now: you have ${money(freeToGive)} free and ${account.username} has ${money(headroom)} of room under their limit.`
        : `Up to ${money(cap)} — the lower of your ${money(freeToGive)} free to give and their ${money(headroom)} of room under a ${money(account.creditLimit)} credit limit.`,
  }
}

/**
 * Every selected row, in the order the run will execute them.
 *
 * Ordering is load-bearing and is the reason this returns an array rather than a
 * map:
 *
 *   1. TAKES FIRST, largest first. A take raises your balance and therefore your
 *      free-to-give, so running takes before gives strictly increases how much
 *      of the give side completes. The other order leaves gives short for money
 *      that was sitting in the very accounts about to be swept.
 *   2. GIVES SECOND, ASCENDING. When free-to-give runs out this maximises the
 *      NUMBER of rows that settle in full and lands the whole shortfall on one
 *      large row, rather than shaving a stub off every row. Eleven closed rows
 *      and one you chase beats twelve rows all still open. It is a judgement
 *      call, so it is written down.
 *
 * The running budget is threaded as a BALANCE, not as a free-to-give figure.
 * `availableToGive` floors at zero, so an account whose balance sits below its
 * exposure has ₹0 free; adding a take's proceeds to that zero would invent
 * credit that the ledger will not honour. Re-deriving free-to-give from the
 * running balance at every row is the only version that survives that case.
 */
export function planSettlement(self: Account, selected: Account[]): PlannedRow[] {
  const takes = selected
    .filter((account) => round2(account.settlement) > 0)
    .sort((a, b) => b.settlement - a.settlement)
  const gives = selected
    .filter((account) => round2(account.settlement) < 0)
    .sort((a, b) => Math.abs(a.settlement) - Math.abs(b.settlement))

  let balance = self.balance
  const exposure = self.exposure
  const rows: PlannedRow[] = []

  for (const account of [...takes, ...gives]) {
    const running = { balance, exposure }
    const freeToGive = availableToGive(balance, exposure)
    const plan = settlementPlan(account, running, freeToGive)
    // Unreachable for a row that passed the sign filters above, but a settled
    // row must never become a zero-amount transfer if it ever does get here.
    if (!plan) continue

    const planned = plan.possible
    const warnings: string[] = []

    if (plan.direction === 'take') {
      // A warning, never a cap. The individual dialog has never blocked this,
      // `transferCredit` has never blocked it, and on a Monday settle the
      // operator's intent is explicitly to sweep the account. Moving less than
      // they asked for, for a reason the ledger would have accepted, is the kind
      // of helpfulness that loses trust. Say it loudly, move the money.
      const after = round2(account.balance - planned)
      if (planned > 0 && after < account.exposure) {
        warnings.push(
          `leaves them with ${money(after)} against ${money(account.exposure)} of open bets — their next bet will be rejected`,
        )
      }
      balance = round2(balance + planned)
    } else {
      balance = round2(balance - planned)
    }

    rows.push({
      account,
      direction: plan.direction,
      intended: plan.wanted,
      planned,
      remaining: round2(plan.wanted - planned),
      capReason: settlementCapReason(account, plan, running, freeToGive),
      warnings,
      skipReason: planned === 0 ? settlementSkipReason(account, plan, running, freeToGive) : null,
    })
  }

  return rows
}

export interface PlanTotals {
  /** What zeroing every row would require. */
  intended: number
  /** What the caps allow. */
  planned: number
  collected: number
  paid: number
  /** Positive = credit coming up to you on balance. */
  net: number
  full: number
  partial: number
  skipped: number
}

/** The summary strip above the plan. Derived, so the strip and the table cannot disagree. */
export function planTotals(rows: PlannedRow[]): PlanTotals {
  let intended = 0
  let planned = 0
  let collected = 0
  let paid = 0
  let full = 0
  let partial = 0
  let skipped = 0

  for (const row of rows) {
    intended += row.intended
    planned += row.planned
    if (row.direction === 'take') collected += row.planned
    else paid += row.planned
    if (row.planned === 0) skipped += 1
    else if (row.planned < row.intended) partial += 1
    else full += 1
  }

  return {
    intended: round2(intended),
    planned: round2(planned),
    collected: round2(collected),
    paid: round2(paid),
    net: round2(collected - paid),
    full,
    partial,
    skipped,
  }
}
